import { useState, useEffect, useMemo } from 'react';
import apiService from '../../services/api.service';
import './AS2Routing.css';

const AS2Routing = () => {
    const [routes, setRoutes] = useState([]);
    const [partners, setPartners] = useState([]);
    const [connectors, setConnectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Form State
    const [formData, setFormData] = useState({
        senderId: '',
        receiverId: '',
        senderSgln: '',
        receiverSgln: '',
        as2ConnectorId: '',
        description: ''
    });

    // Search states for partner dropdowns
    const [senderSearch, setSenderSearch] = useState('');
    const [receiverSearch, setReceiverSearch] = useState('');

    const fetchRoutes = async () => {
        try {
            const response = await apiService.get('/as2/routes');
            if (response.success) {
                setRoutes(response.data);
            } else {
                setError('Failed to fetch routes');
            }
        } catch (err) {
            console.error('Error fetching routes:', err);
            setError('An error occurred while fetching routes');
        } finally {
            setLoading(false);
        }
    };

    const fetchPartners = async () => {
        try {
            const response = await apiService.get('/partners');
            if (response.success) {
                setPartners(response.data);
            }
        } catch (err) {
            console.error('Error fetching partners:', err);
        }
    };

    const fetchConnectors = async () => {
        try {
            const data = await apiService.get('/as2/connectors');
            if (Array.isArray(data)) {
                setConnectors(data);
            }
        } catch (err) {
            console.error('Error fetching connectors:', err);
        }
    };

    useEffect(() => {
        fetchRoutes();
        fetchPartners();
        fetchConnectors();
    }, []);

    // Filter partners based on search
    const filteredSenders = useMemo(() => {
        if (!senderSearch) return partners;
        const search = senderSearch.toLowerCase();
        return partners.filter(p =>
            p.name?.toLowerCase().includes(search) ||
            p.gln?.toLowerCase().includes(search)
        );
    }, [partners, senderSearch]);

    const filteredReceivers = useMemo(() => {
        if (!receiverSearch) return partners;
        const search = receiverSearch.toLowerCase();
        return partners.filter(p =>
            p.name?.toLowerCase().includes(search) ||
            p.gln?.toLowerCase().includes(search)
        );
    }, [partners, receiverSearch]);

    // Filter routes for display
    const filteredRoutes = useMemo(() => {
        if (!searchQuery) return routes;
        const search = searchQuery.toLowerCase();
        return routes.filter(r =>
            r.sender_name?.toLowerCase().includes(search) ||
            r.receiver_name?.toLowerCase().includes(search) ||
            r.sender_id?.toLowerCase().includes(search) ||
            r.receiver_id?.toLowerCase().includes(search) ||
            r.description?.toLowerCase().includes(search)
        );
    }, [routes, searchQuery]);

    // Get partner/connector name by ID
    const getPartnerName = (gln) => {
        const partner = partners.find(p => p.gln === gln);
        return partner?.name || gln;
    };

    const getConnectorName = (id) => {
        const connector = connectors.find(c => c.connectorId === id);
        return connector?.name || id;
    };

    const handleOpenModal = (route = null) => {
        if (route) {
            setEditingRoute(route);
            setFormData({
                senderId: route.sender_id,
                receiverId: route.receiver_id,
                senderSgln: route.sender_sgln || '',
                receiverSgln: route.receiver_sgln || '',
                as2ConnectorId: route.as2_connector_id,
                description: route.description || ''
            });
        } else {
            setEditingRoute(null);
            setFormData({
                senderId: '',
                receiverId: '',
                senderSgln: '',
                receiverSgln: '',
                as2ConnectorId: '',
                description: ''
            });
        }
        setSenderSearch('');
        setReceiverSearch('');
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            let response;
            if (editingRoute) {
                response = await apiService.put(`/as2/routes/${editingRoute.id}`, formData);
            } else {
                response = await apiService.post('/as2/routes', formData);
            }

            if (response.success) {
                fetchRoutes();
                setIsModalOpen(false);
            } else {
                alert(response.message || 'Failed to save route');
            }
        } catch (err) {
            console.error('Error saving route:', err);
            alert('An error occurred while saving');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this route?')) return;

        try {
            const response = await apiService.delete(`/as2/routes/${id}`);
            if (response.success) {
                setRoutes(routes.filter(r => r.id !== id));
            } else {
                alert('Failed to delete route');
            }
        } catch (err) {
            console.error('Error deleting route:', err);
            alert('An error occurred while deleting');
        }
    };

    const selectPartner = (type, partner) => {
        if (type === 'sender') {
            setFormData({
                ...formData,
                senderId: partner.gln,
                senderSgln: partner.sgln || ''
            });
            setSenderSearch('');
        } else {
            setFormData({
                ...formData,
                receiverId: partner.gln,
                receiverSgln: partner.sgln || ''
            });
            setReceiverSearch('');
        }
    };

    if (loading) return <div className="loading">Loading routes...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <>
            <header className="top-header">
                <div className="breadcrumb">
                    <span>Partner Connections</span>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">AS2 Routing</span>
                </div>
            </header>
            <div className="as2-routing-page">
                {/* Page Header */}
                <div className="page-header">
                    <div>
                        <h1>AS2 Routing Rules</h1>
                        <p className="subtitle">Define how messages flow between trading partners</p>
                    </div>
                    <button className="primary-btn" onClick={() => handleOpenModal()}>
                        + New Route
                    </button>
                </div>

                {/* Search */}
                <div className="search-bar">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <input
                        type="text"
                        placeholder="Search routes..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* Routes Table */}
                <div className="routes-table-container">
                    <table className="routes-table">
                        <thead>
                            <tr>
                                <th>Sender</th>
                                <th></th>
                                <th>Receiver</th>
                                <th>Connector</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRoutes.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="empty-cell">
                                        <div className="empty-state">
                                            <span>📭</span>
                                            <p>No routing rules yet</p>
                                            <button className="primary-btn small" onClick={() => handleOpenModal()}>
                                                Create your first route
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredRoutes.map(route => (
                                    <tr key={route.id}>
                                        <td className="partner-cell">
                                            <span className="partner-name">{route.sender_name || getPartnerName(route.sender_id)}</span>
                                            {route.sender_sgln && <span className="partner-sgln">{route.sender_sgln}</span>}
                                        </td>
                                        <td className="arrow-cell">→</td>
                                        <td className="partner-cell">
                                            <span className="partner-name">{route.receiver_name || getPartnerName(route.receiver_id)}</span>
                                            {route.receiver_sgln && <span className="partner-sgln">{route.receiver_sgln}</span>}
                                        </td>
                                        <td>
                                            <span className="connector-badge">{route.connector_name || getConnectorName(route.as2_connector_id)}</span>
                                        </td>
                                        <td className="desc-cell">{route.description || '—'}</td>
                                        <td className="actions-cell">
                                            <button className="action-btn edit" onClick={() => handleOpenModal(route)}>Edit</button>
                                            <button className="action-btn delete" onClick={() => handleDelete(route.id)}>Delete</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Create/Edit Modal */}
                {isModalOpen && (
                    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
                        <div className="routing-modal">
                            <div className="modal-header">
                                <h2>{editingRoute ? 'Edit Routing Rule' : 'Create Routing Rule'}</h2>
                                <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
                            </div>

                            <div className="modal-body">
                                {/* Visual Route Flow */}
                                <div className="route-flow-visual">
                                    <div className={`flow-box sender ${formData.senderId ? 'filled' : ''}`}>
                                        <span className="flow-label">FROM</span>
                                        <span className="flow-value">{formData.senderId ? getPartnerName(formData.senderId) : 'Select sender'}</span>
                                    </div>
                                    <div className="flow-arrow">→</div>
                                    <div className={`flow-box receiver ${formData.receiverId ? 'filled' : ''}`}>
                                        <span className="flow-label">TO</span>
                                        <span className="flow-value">{formData.receiverId ? getPartnerName(formData.receiverId) : 'Select receiver'}</span>
                                    </div>
                                </div>

                                {/* Sender Selection */}
                                <div className="form-group">
                                    <label>Sender Partner</label>
                                    <div className="partner-selector">
                                        {formData.senderId ? (
                                            <div className="selected-partner">
                                                <span>{getPartnerName(formData.senderId)}</span>
                                                <button onClick={() => setFormData({ ...formData, senderId: '' })}>Change</button>
                                            </div>
                                        ) : (
                                            <>
                                                <input
                                                    type="text"
                                                    placeholder="Type to search partners..."
                                                    value={senderSearch}
                                                    onChange={(e) => setSenderSearch(e.target.value)}
                                                />
                                                {senderSearch && (
                                                    <div className="dropdown">
                                                        {filteredSenders.length === 0 ? (
                                                            <div className="dropdown-empty">No partners found</div>
                                                        ) : (
                                                            filteredSenders.slice(0, 5).map(p => (
                                                                <div key={p.id} className="dropdown-item" onClick={() => selectPartner('sender', p)}>
                                                                    <span className="item-name">{p.name}</span>
                                                                    <span className="item-gln">{p.gln}</span>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Sender SGLN */}
                                <div className="form-group">
                                    <label>Sender SGLN <span className="optional">(optional)</span></label>
                                    <input
                                        type="text"
                                        value={formData.senderSgln}
                                        onChange={(e) => setFormData({ ...formData, senderSgln: e.target.value })}
                                        placeholder="urn:epc:id:sgln:..."
                                    />
                                </div>

                                {/* Receiver Selection */}
                                <div className="form-group">
                                    <label>Receiver Partner</label>
                                    <div className="partner-selector">
                                        {formData.receiverId ? (
                                            <div className="selected-partner">
                                                <span>{getPartnerName(formData.receiverId)}</span>
                                                <button onClick={() => setFormData({ ...formData, receiverId: '' })}>Change</button>
                                            </div>
                                        ) : (
                                            <>
                                                <input
                                                    type="text"
                                                    placeholder="Type to search partners..."
                                                    value={receiverSearch}
                                                    onChange={(e) => setReceiverSearch(e.target.value)}
                                                />
                                                {receiverSearch && (
                                                    <div className="dropdown">
                                                        {filteredReceivers.length === 0 ? (
                                                            <div className="dropdown-empty">No partners found</div>
                                                        ) : (
                                                            filteredReceivers.slice(0, 5).map(p => (
                                                                <div key={p.id} className="dropdown-item" onClick={() => selectPartner('receiver', p)}>
                                                                    <span className="item-name">{p.name}</span>
                                                                    <span className="item-gln">{p.gln}</span>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Receiver SGLN */}
                                <div className="form-group">
                                    <label>Receiver SGLN <span className="optional">(optional)</span></label>
                                    <input
                                        type="text"
                                        value={formData.receiverSgln}
                                        onChange={(e) => setFormData({ ...formData, receiverSgln: e.target.value })}
                                        placeholder="urn:epc:id:sgln:..."
                                    />
                                </div>

                                {/* Connector */}
                                <div className="form-group">
                                    <label>AS2 Connector</label>
                                    <select
                                        value={formData.as2ConnectorId}
                                        onChange={(e) => setFormData({ ...formData, as2ConnectorId: e.target.value })}
                                    >
                                        <option value="">Choose a connector...</option>
                                        {connectors.map(c => (
                                            <option key={c.connectorId} value={c.connectorId}>
                                                {c.name || c.connectorId}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Description */}
                                <div className="form-group">
                                    <label>Description <span className="optional">(optional)</span></label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Add a note about this route..."
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                <button
                                    className="save-btn"
                                    onClick={handleSave}
                                    disabled={!formData.senderId || !formData.receiverId || !formData.as2ConnectorId}
                                >
                                    {editingRoute ? 'Save Changes' : 'Create Route'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AS2Routing;
